import { useMemo, useRef, useEffect, useCallback } from 'react'
import { sanitizeHtml, isHtmlContent, linkifyHtml, textToHtml } from '@/lib/sanitizeHtml'
import { linkifyText } from '@/lib/linkify'
import { highlightHtml } from '@/lib/textSearch'

interface LightboxImage {
  url: string
  filename?: string
}

interface MessageBodyProps {
  content: string
  className?: string
  onImageClick?: (images: LightboxImage[], index: number) => void
  /** Search term to highlight inside the rendered text (case/accent-insensitive) */
  highlight?: string
}

/**
 * Renders message body content with support for both HTML and plain text.
 * - HTML content (from Intercom imports, rich editor, macros) is rendered with formatting
 * - Plain text content is displayed with whitespace preserved
 * - All HTML is sanitized to prevent XSS attacks
 * - Images are clickable and open in lightbox when onImageClick is provided
 * - Occurrences of `highlight` are wrapped in <mark>, e.g. for search previews
 */
export default function MessageBody({ content, className = '', onImageClick, highlight }: MessageBodyProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Sanitizing parses the DOM, so it depends on the content only; the search
  // highlight changes per keystroke and is layered on top separately.
  // Plain text is promoted to HTML when highlighting so both paths share it.
  const wantsHighlight = Boolean(highlight)
  const { isHtml, html } = useMemo(() => {
    if (!content) return { isHtml: false, html: '' }
    if (isHtmlContent(content)) return { isHtml: true, html: linkifyHtml(sanitizeHtml(content)) }
    if (wantsHighlight) return { isHtml: true, html: linkifyHtml(textToHtml(content)) }
    return { isHtml: false, html: content }
  }, [content, wantsHighlight])

  const sanitizedContent = useMemo(
    () => (isHtml && highlight ? highlightHtml(html, highlight) : html),
    [isHtml, html, highlight]
  )

  // Handle image clicks for lightbox
  const handleImageClick = useCallback((e: MouseEvent) => {
    if (!onImageClick) return

    const target = e.target as HTMLElement
    if (target.tagName !== 'IMG') return

    e.preventDefault()
    e.stopPropagation()

    // Get all images in the container
    const container = containerRef.current
    if (!container) return

    const images = Array.from(container.querySelectorAll('img'))
    const lightboxImages: LightboxImage[] = images.map(img => ({
      url: img.src,
      filename: img.alt || undefined,
    }))

    const clickedIndex = images.indexOf(target as HTMLImageElement)
    if (clickedIndex >= 0 && lightboxImages.length > 0) {
      onImageClick(lightboxImages, clickedIndex)
    }
  }, [onImageClick])

  // Add click listeners to images
  useEffect(() => {
    const container = containerRef.current
    if (!container || !isHtml || !onImageClick) return

    container.addEventListener('click', handleImageClick)
    return () => container.removeEventListener('click', handleImageClick)
  }, [isHtml, sanitizedContent, handleImageClick, onImageClick])

  if (!content) return null

  if (isHtml) {
    return (
      <div
        ref={containerRef}
        className={`signal-message-body prose prose-sm max-w-none prose-p:my-0 prose-p:leading-normal prose-img:my-0 [&_p:empty]:min-h-[1em] ${onImageClick ? '[&_img]:cursor-pointer' : ''} ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        style={{
          // Ensure images display properly
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
        }}
      />
    )
  }

  // Plain text: preserve whitespace, line breaks, and linkify URLs
  return (
    <p className={`whitespace-pre-wrap ${className}`}>
      {linkifyText(sanitizedContent)}
    </p>
  )
}

/**
 * CSS classes for styling HTML content within messages.
 * Apply these to the parent container or use Tailwind's prose classes.
 */
export const messageBodyStyles = `
  /* Links */
  a { color: #2563eb; text-decoration: underline; }
  a:hover { color: #1d4ed8; }

  /* Headings */
  h1 { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem; }
  h2 { font-size: 1.25rem; font-weight: 600; margin-bottom: 0.5rem; }
  h3 { font-size: 1.125rem; font-weight: 600; margin-bottom: 0.5rem; }

  /* Code */
  code {
    background-color: #f3f4f6;
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: ui-monospace, monospace;
    font-size: 0.875rem;
  }
  pre {
    background-color: #F8F8F7;
    color: #111827;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.875em;
    line-height: 1.6;
    padding: 1rem;
    border: 0;
    border-radius: 0.75rem;
    box-shadow: none;
    overflow-x: auto;
    white-space: pre-wrap;
  }
  pre code {
    background-color: transparent;
    padding: 0;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
    margin: 0.5rem 0;
  }

  /* Lists */
  ul, ol { padding-left: 1.5rem; margin: 0.5rem 0; }
  ul { list-style-type: disc; }
  ol { list-style-type: decimal; }
  li { margin: 0.25rem 0; }

  /* Blockquote */
  blockquote {
    border-left: 3px solid #d1d5db;
    padding-left: 1rem;
    margin: 0.5rem 0;
    color: #6b7280;
    font-style: italic;
  }

  /* Paragraphs */
  p { margin: 0.5rem 0; }
  p:first-child { margin-top: 0; }
  p:last-child { margin-bottom: 0; }
`
